import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import {
  ShieldCheck, ShieldWarning, ArrowClockwise, CheckCircle, Buildings,
  IdentificationCard, Hash, User, Calendar, ArrowsClockwise,
} from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminSignupReviewPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await client.get("/admin/signup-review");
      setItems(data);
    } catch {
      setLoadError(true);
      toast.error("Failed to load signup review queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    else if (user) setLoading(false);
  }, [user]);

  if (user && !user.is_admin) return <Navigate to="/app/dashboard" replace />;

  const setBusy = (id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const approve = async (id) => {
    if (!window.confirm("Approve this signup? The account will be marked reviewed and safe.")) return;
    setBusy(id, true);
    try {
      await client.post(`/admin/signup-review/${id}/approve`);
      setItems((prev) => prev.filter((u) => u.id !== id));
      toast.success("Approved");
    } catch {
      toast.error("Approve failed");
    } finally {
      setBusy(id, false);
    }
  };

  const reverify = async (id) => {
    setBusy(id, true);
    try {
      const { data } = await client.post(`/admin/signup-review/${id}/re-verify`);
      if (data?.fmcsa?.verified) {
        setItems((prev) => prev.filter((u) => u.id !== id));
        toast.success(`Verified: ${data.fmcsa.legal_name || "carrier"}`);
      } else {
        // Update the row's reason in place
        setItems((prev) => prev.map((u) => (u.id === id ? { ...u, fmcsa_verify_reason: data?.fmcsa?.reason || u.fmcsa_verify_reason } : u)));
        toast.error(`Still not verified: ${data?.fmcsa?.reason || "no match"}`);
      }
    } catch {
      toast.error("Re-verify failed");
    } finally {
      setBusy(id, false);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto" data-testid="admin-signup-review-page">
      <PageHeader
        overline="ADMIN"
        title="Signup Review Queue"
        subtitle={`${items.length} account${items.length === 1 ? "" : "s"} flagged for review`}
        action={
          <button
            data-testid="review-refresh-btn"
            onClick={load}
            className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        }
      />

      {/* Explainer */}
      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center shrink-0">
          <ShieldWarning size={16} weight="bold" className="text-[#FF5A1F]" />
        </div>
        <div className="text-sm text-neutral-300 leading-relaxed">
          These accounts signed up with an MC# or DOT# that FMCSA public records couldn't verify.
          Approve if legitimate (data quirk / new carrier not indexed yet) or Re-verify to retry
          the lookup live in case FMCSA has updated.
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Loading…</div>
        ) : loadError ? (
          <div
            className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-12 text-center"
            data-testid="review-load-error"
          >
            <ShieldWarning size={32} weight="light" className="text-rose-400 mx-auto mb-3" />
            <div className="overline mb-2 text-rose-300">ACCESS DENIED</div>
            <p className="text-neutral-400 text-sm max-w-sm mx-auto">
              We couldn't load the review queue for this account.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-12 text-center"
            data-testid="review-empty"
          >
            <CheckCircle size={32} weight="light" className="text-emerald-400 mx-auto mb-3" />
            <div className="overline mb-2 text-emerald-300">ALL CLEAR</div>
            <p className="text-neutral-400 text-sm">
              No signups pending review. New unverified signups will land here automatically.
            </p>
          </div>
        ) : items.map((u) => (
          <div
            key={u.id}
            data-testid={`review-item-${u.id.slice(0, 8)}`}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Buildings size={14} className="text-neutral-500 shrink-0" />
                  <div className="text-base font-semibold text-white truncate">
                    {u.company_name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <User size={12} />
                  <span className="truncate">{u.full_name || "—"}</span>
                  <span className="text-neutral-600">·</span>
                  <a href={`mailto:${u.email}`} className="hover:text-[#FF5A1F] truncate">
                    {u.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-neutral-500 shrink-0">
                <Calendar size={11} />
                {timeAgo(u.created_at)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <FactChip icon={IdentificationCard} label="MC#" value={u.fmcsa_data?.docket_number || u?.mc_number || "—"} />
              <FactChip icon={Hash} label="USDOT#" value={u.fmcsa_data?.dot_number || u?.dot_number || "—"} />
              <FactChip
                icon={ShieldWarning}
                label="FMCSA reason"
                value={u.fmcsa_verify_reason || "Not verified"}
                tone="amber"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => reverify(u.id)}
                disabled={busyIds.has(u.id)}
                data-testid={`review-reverify-${u.id.slice(0, 8)}`}
                className="rounded-full px-4 py-2 text-xs inline-flex items-center gap-2 border border-white/10 hover:border-white/25 text-neutral-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowsClockwise size={12} weight="bold" />
                {busyIds.has(u.id) ? "…" : "Re-verify"}
              </button>
              <button
                onClick={() => approve(u.id)}
                disabled={busyIds.has(u.id)}
                data-testid={`review-approve-${u.id.slice(0, 8)}`}
                className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShieldCheck size={12} weight="bold" />
                {busyIds.has(u.id) ? "…" : "Approve"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FactChip({ icon: Icon, label, value, tone = "neutral" }) {
  const bg =
    tone === "amber"
      ? "border-amber-500/20 bg-amber-500/5"
      : "border-white/10 bg-white/[0.02]";
  const iconColor = tone === "amber" ? "text-amber-400" : "text-neutral-500";
  return (
    <div className={`rounded-lg border px-3 py-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5 flex items-center gap-1">
        <Icon size={10} className={iconColor} />
        {label}
      </div>
      <div className="text-xs text-neutral-200 font-mono truncate" title={value}>
        {value}
      </div>
    </div>
  );
}
